/**
 * Kendall's rank correlation, τ-a and τ-b.
 *
 * Every pair of observations (i, j) is concordant when both variables order it the same
 * way, discordant when they order it opposite ways, and tied otherwise. With C
 * concordant pairs, D discordant pairs and n₀ = n(n − 1)/2 pairs in all:
 *
 *   τ-a = (C − D) / n₀
 *   τ-b = (C − D) / √((n₀ − n₁)(n₀ − n₂))   — n₁, n₂ count the pairs tied on x, on y
 *
 * Significance follows R's `cor.test(method = "kendall")`, which the tests pin:
 *
 * - With no ties and n < 50 the p-value is exact, from the distribution of the number of
 *   concordant pairs over all n! orderings (the inversion-count distribution, built by a
 *   convolution recurrence).
 * - Otherwise a normal approximation to S = C − D with the tie-corrected variance of
 *   Kendall (1970), optionally with a continuity correction.
 *
 * S is the same for τ-a and τ-b, so both share one p-value.
 */

import { averageRanks, pearson } from "@/lib/stats/descriptive";
import { normalCdf } from "@/lib/stats/distributions";

export type Alternative = "two-sided" | "greater" | "less";

export interface PairCounts {
  concordant: number;
  discordant: number;
  /** Pairs tied on x but not on y. */
  tiedX: number;
  /** Pairs tied on y but not on x. */
  tiedY: number;
  /** Pairs tied on both. */
  tiedBoth: number;
}

/** O(n²) pair classification. */
export function countPairs(
  xs: readonly number[],
  ys: readonly number[]
): PairCounts {
  const counts: PairCounts = {
    concordant: 0,
    discordant: 0,
    tiedX: 0,
    tiedY: 0,
    tiedBoth: 0,
  };
  const n = xs.length;
  for (let i = 0; i < n - 1; i++) {
    for (let j = i + 1; j < n; j++) {
      const dx = Math.sign(xs[i] - xs[j]);
      const dy = Math.sign(ys[i] - ys[j]);
      if (dx === 0 && dy === 0) {
        counts.tiedBoth += 1;
      } else if (dx === 0) {
        counts.tiedX += 1;
      } else if (dy === 0) {
        counts.tiedY += 1;
      } else if (dx === dy) {
        counts.concordant += 1;
      } else {
        counts.discordant += 1;
      }
    }
  }
  return counts;
}

export type PairKind =
  | "concordant"
  | "discordant"
  | "tiedX"
  | "tiedY"
  | "tiedBoth";

export interface PairRow {
  i: number;
  j: number;
  kind: PairKind;
}

/** Every pair, classified — for the teaching table on small samples. */
export function classifyPairs(
  xs: readonly number[],
  ys: readonly number[]
): PairRow[] {
  const rows: PairRow[] = [];
  for (let i = 0; i < xs.length - 1; i++) {
    for (let j = i + 1; j < xs.length; j++) {
      const dx = Math.sign(xs[i] - xs[j]);
      const dy = Math.sign(ys[i] - ys[j]);
      let kind: PairKind = dx === dy ? "concordant" : "discordant";
      if (dx === 0 && dy === 0) {
        kind = "tiedBoth";
      } else if (dx === 0) {
        kind = "tiedX";
      } else if (dy === 0) {
        kind = "tiedY";
      }
      rows.push({ i, j, kind });
    }
  }
  return rows;
}

/** Sizes of the tie groups of a variable (groups of two or more equal values). */
function tieSizes(values: readonly number[]): number[] {
  const counts = new Map<number, number>();
  for (const value of values) {
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  return [...counts.values()].filter((count) => count > 1);
}

/**
 * P(Q = k) for the number of inversions Q of a uniformly random permutation of n items,
 * k = 0 … n(n − 1)/2. The recurrence p_n(k) = (1/n) Σ_{j<n} p_{n−1}(k − j) is evaluated
 * with a running window sum, so it costs O(n · n²) and never forms n!.
 */
export function inversionDistribution(n: number): number[] {
  let probabilities = [1];
  for (let m = 2; m <= n; m++) {
    const size = (m * (m - 1)) / 2 + 1;
    const next = new Array<number>(size).fill(0);
    let window = 0;
    for (let k = 0; k < size; k++) {
      window += probabilities[k] ?? 0;
      if (k >= m) {
        window -= probabilities[k - m] ?? 0;
      }
      next[k] = window / m;
    }
    probabilities = next;
  }
  return probabilities;
}

/** The exact null distribution is used below this sample size when there are no ties. */
export const EXACT_LIMIT = 50;

export interface KendallReport {
  n: number;
  pairs: number;
  counts: PairCounts;
  /** S = C − D. */
  s: number;
  tauA: number;
  tauB: number;
  hasTies: boolean;
  /** Pairs tied on x (n₁) and on y (n₂), counting pairs tied on both. */
  tiesX: number;
  tiesY: number;
  method: "exact" | "normal";
  /** z for the normal approximation; null when the exact test was used. */
  z: number | null;
  /** The variance of S used by the normal approximation. */
  varianceS: number;
  p: number;
  spearmanRho: number;
  pearsonR: number;
}

function pairsOf(sizes: readonly number[]): number {
  let total = 0;
  for (const size of sizes) {
    total += (size * (size - 1)) / 2;
  }
  return total;
}

/** Var(S) with Kendall's tie corrections — the formula in R's `cor.test`. */
export function varianceOfS(
  n: number,
  xTies: readonly number[],
  yTies: readonly number[]
): number {
  const sum = (sizes: readonly number[], f: (t: number) => number) =>
    sizes.reduce((total, size) => total + f(size), 0);
  const v0 = n * (n - 1) * (2 * n + 5);
  const vt = sum(xTies, (t) => t * (t - 1) * (2 * t + 5));
  const vu = sum(yTies, (t) => t * (t - 1) * (2 * t + 5));
  const v1 = sum(xTies, (t) => t * (t - 1)) * sum(yTies, (t) => t * (t - 1));
  const v2 =
    sum(xTies, (t) => t * (t - 1) * (t - 2)) *
    sum(yTies, (t) => t * (t - 1) * (t - 2));
  let variance = (v0 - vt - vu) / 18 + v1 / (2 * n * (n - 1));
  if (n > 2) {
    variance += v2 / (9 * n * (n - 1) * (n - 2));
  }
  return variance;
}

function exactP(
  concordant: number,
  n: number,
  alternative: Alternative
): number {
  const probabilities = inversionDistribution(n);
  let lower = 0; // P(C ≤ concordant)
  let upper = 0; // P(C ≥ concordant)
  for (const [k, probability] of probabilities.entries()) {
    if (k <= concordant) {
      lower += probability;
    }
    if (k >= concordant) {
      upper += probability;
    }
  }
  if (alternative === "greater") {
    return Math.min(1, upper);
  }
  if (alternative === "less") {
    return Math.min(1, lower);
  }
  return Math.min(1, 2 * Math.min(lower, upper));
}

function normalP(z: number, alternative: Alternative): number {
  if (alternative === "greater") {
    return 1 - normalCdf(z);
  }
  if (alternative === "less") {
    return normalCdf(z);
  }
  return 2 * Math.min(normalCdf(z), 1 - normalCdf(z));
}

export function kendallReport(
  xs: readonly number[],
  ys: readonly number[],
  options: { alternative?: Alternative; continuity?: boolean } = {}
): KendallReport | null {
  const n = xs.length;
  if (n < 3 || ys.length !== n) {
    return null;
  }
  const counts = countPairs(xs, ys);
  const pairs = (n * (n - 1)) / 2;
  const xTies = tieSizes(xs);
  const yTies = tieSizes(ys);
  const tiesX = pairsOf(xTies);
  const tiesY = pairsOf(yTies);
  const denominator = Math.sqrt((pairs - tiesX) * (pairs - tiesY));
  if (denominator === 0) {
    return null;
  }
  const s = counts.concordant - counts.discordant;
  const hasTies = tiesX > 0 || tiesY > 0;
  const alternative = options.alternative ?? "two-sided";
  const varianceS = varianceOfS(n, xTies, yTies);
  const exact = !hasTies && n < EXACT_LIMIT;

  let z: number | null = null;
  let p: number;
  if (exact) {
    p = exactP(counts.concordant, n, alternative);
  } else {
    let adjusted = s;
    if (options.continuity) {
      adjusted = Math.sign(s) * (Math.abs(s) - 1);
    }
    z = adjusted / Math.sqrt(varianceS);
    p = normalP(z, alternative);
  }
  return {
    n,
    pairs,
    counts,
    s,
    tauA: s / pairs,
    tauB: s / denominator,
    hasTies,
    tiesX,
    tiesY,
    method: exact ? "exact" : "normal",
    z,
    varianceS,
    p,
    spearmanRho: pearson(averageRanks(xs), averageRanks(ys)),
    pearsonR: pearson(xs, ys),
  };
}
